<template>
	<div v-if="equivalencyMap">
		<q-card class="equivalency-map-card q-mb-sm" v-for="(result, i) in equivalencyMap.TransferMap" :key="i">
			<q-item>
				<q-item-side :avatar="result.FromSchool.Logo" v-if="result.FromSchool.Logo" />
				<q-item-main>
					<q-item-tile label>{{ result.FromSchool.InstitutionName }}</q-item-tile>
					<q-item-tile sublabel>Your transferable courses to {{ result.ToSchool.InstitutionName }}</q-item-tile>
				</q-item-main>
			</q-item>

			<q-card-main class="equiv-map-container">
				<table class="equiv-map">
					<thead>
						<tr>
							<th style="width: 1.2rem"></th>
							<th>{{ getProviderCourseType(result) }}</th>
							<th style="width: 1.2rem"></th>
							<th style="width: 50%">
								{{ result.ToSchool.InstitutionName }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(equivalency, j) in result.Equivalencies" :key="j">
							<td>
								<q-icon v-if="getStatusIcon(equivalency)" :name="getStatusIcon(equivalency)" color="primary" />
							</td>
							<td>
								<ul>
									<li v-for="(takenCourse, k) in equivalency.TakenCourses" :key="k">
										{{ takenCourse.CourseId }}
										<span v-if="takenCourse.GradeReceived">
											({{ takenCourse.GradeReceived }})
										</span>
									</li>
								</ul>
							</td>
							<td>
								<q-icon name="arrow_right_alt" color="faded" />
							</td>
							<td>
								<ul v-if="equivalency.EquivalentCourses.length">
									<li v-for="(equivalentCourse, k) in equivalency.EquivalentCourses" :key="k">
										<div class="float-right">
											{{ equivalentCourse.CreditDisplay }} cr
										</div>
										<div style="overflow: auto">
											{{ equivalentCourse.CourseId }}
											<div class="q-caption text-weight-light">
												{{ equivalentCourse.CourseTitle }}
											</div>
										</div>
									</li>
								</ul>
								<div class="text-faded">
									<span v-if="!equivalency.EquivalentCourses.length">
									No evaluated courses
									</span>
									<small class="equiv-hint" v-if="equivalency.EquivalentCourses.length">
										{{ getStatusText(result, equivalency) }}
									</small>
								</div>
							</td>
						</tr>
					</tbody>
					<tfoot>
						<tr>
							<td colspan="3"></td>
							<td>
								<div class="float-left">
									<strong>{{totalCoursesDisplay(result)}}</strong>
								</div>
								<div class="float-right">
									<strong>{{totalCreditDisplay(result)}}</strong>
								</div>
							</td>
						</tr>
					</tfoot>
				</table>
			</q-card-main>
		</q-card>
	</div>

</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import EquivalencyMap from '@/components/equivalency-map/EquivalencyMap.vue';
import { CourseEntry } from '@/models';
import config from '@/models/services/config';
import { client as collegeTransferClient, IWillMyCreditsTransferResponse } from '@/models/services/collegetransfer/';
import {
	IWmctMapItem,
	IWillMyCreditsTransferRequest,
	IWmctEquivalency,
} from '@/models/services/collegetransfer/entities';

@Component({
	computed: mapGetters('wmct', [
		'isDuplicateCoursesOnSource',
		'isDuplicateCoursesOnTarget',
		'getTotalCreditsForTargetSchool',
		'getTotalCoursesForTargetSchool',
		'getStatusIcon',
		'getStatusText',
	])
})
export default class WillMyCreditsTransfer extends Vue {
	private isDuplicateCoursesOnSource!: boolean;
	private isDuplicateCoursesOnTarget!: boolean;
	private getTotalCreditsForTargetSchool!: (val: IWmctMapItem) => number;
	private getTotalCoursesForTargetSchool!: (val: IWmctMapItem) => number;
	private getStatusIcon!: (val: IWmctEquivalency) => string | null;
	private getStatusText!: (mapItem: IWmctMapItem, val: IWmctEquivalency) => string;

	public get equivalencyMap() {
		return this.$store.state.wmct.equivalencyMap;
	}

	public getProviderCourseType(val: IWmctMapItem) {
		return val.Equivalencies.every((equiv) => equiv.TakenCourses.every((tc) => tc.CourseType === 1))
			? 'Taken Assessments'
			: 'Taken Courses';
	}

	public totalCreditDisplay(val: IWmctMapItem): string {
		const hasSourceDupes = this.isDuplicateCoursesOnSource;
		const hasTargetDupes = this.isDuplicateCoursesOnTarget;

		const totalCredits = this.getTotalCreditsForTargetSchool(val);

		if (totalCredits > 0 && (hasSourceDupes || hasTargetDupes)) {
			return 'Varies';
		}

		return `${totalCredits.toFixed(2)} cr`;
	}

	public totalCoursesDisplay(val: IWmctMapItem) {
		const hasSourceDupes = this.isDuplicateCoursesOnSource;
		const hasTargetDupes = this.isDuplicateCoursesOnTarget;
		const totalCourses = this.getTotalCoursesForTargetSchool(val);
		const transferOptionsLabel = totalCourses !== 1 ? 'Transfer Options' : 'Transfer Option';
		const coursesLabel = totalCourses !== 1 ? 'Courses' : 'Course';

		const suffix = totalCourses > 0 && (hasSourceDupes || hasTargetDupes) ? transferOptionsLabel : coursesLabel;

		return `${this.getTotalCoursesForTargetSchool(val)} ${suffix}`;
	}

}
</script>

<style>
	.q-card-main.equiv-map-container {
		padding: 12px;
	}

	.equivalency-map-card .q-item-avatar {
		border-radius: 0;
	}

	.equiv-map {
		border-collapse: collapse;
		width: 100%;
	}

	.equiv-map th {
		text-align: left;
	}

	.equiv-map td {
		vertical-align: top;
		padding: .5rem 0;
		font-size: .8rem;
	}

	.equiv-map tbody td {
		border-bottom: 1px solid #ccc;
	}

	.equiv-map ul {
		padding: 0;
		margin: 0;
	}

	.equiv-map ul li {
		list-style-type: none;
	}

	.equiv-map ul li:not(:first-child):before {
		content: 'and';
		font-size: x-small;
		color: #757575;
	}

	.equiv-hint {
		display: block;
		line-height: .75rem;
		margin-top: .2rem;
	}

</style>